// Ultimate Card Game (UCG) - Sul Homeworld Battlefield Background
//
// Second battlefield backdrop (js/engine/maps.js selects between this and
// the original js/engine/render/background.js "human" map). Ported from the
// standalone art prototype at sandbox/sul-map/ (that folder documents the
// design iteration this went through) into the real renderer's five-method
// contract: renderSulStaticBackground/renderSulStaticForeground are baked
// once into the offscreen world-space canvases by
// Renderer.ensureStaticBackground() (see renderer.js), while
// renderSulDynamicBackground/renderSulDynamicForeground/
// renderSulForegroundCritters run every frame on top of that cached bake -
// same split the human map uses, so this mixes onto the same prototype
// without its own caching logic.
//
// The lane (battlefield.laneY..laneY+laneHeight, where units actually
// fight) stays trampled/bare: rocky perspective-scaled rubble, plasma-scar
// traces, a grained ground texture - no living plant or critter ever
// renders there. All Sul flora/fauna lives on the foreground shelf below
// it, exactly where the human map keeps its own ambient critters.

function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function resolveBounds(renderer, customBounds) {
  const bounds = customBounds || (renderer.viewport ? renderer.viewport.getVisibleBounds() : null);
  const rawTop = bounds ? (typeof bounds.top === 'number' ? bounds.top : (typeof bounds.visibleTop === 'number' ? bounds.visibleTop : -40)) : -40;
  const rawBottom = bounds ? (typeof bounds.bottom === 'number' ? bounds.bottom : (typeof bounds.visibleBottom === 'number' ? bounds.visibleBottom : (renderer.height + 40))) : (renderer.height + 40);
  const rawLeft = bounds ? (typeof bounds.left === 'number' ? bounds.left : (typeof bounds.visibleLeft === 'number' ? bounds.visibleLeft : -40)) : -40;
  const rawRight = bounds ? (typeof bounds.right === 'number' ? bounds.right : (typeof bounds.visibleRight === 'number' ? bounds.visibleRight : (renderer.width + 40))) : (renderer.width + 40);
  return {
    visibleTop: customBounds ? rawTop : Math.min(0, rawTop - 20),
    visibleBottom: customBounds ? rawBottom : Math.max(renderer.height, rawBottom + 20),
    visibleLeft: customBounds ? rawLeft : Math.min(0, rawLeft - 20),
    visibleRight: customBounds ? rawRight : Math.max(renderer.width, rawRight + 20)
  };
}

// --- Static layout (pyramids/ridges/rocks/plants/floor texture) ----------
// Built once per (mapSeed, laneY, laneHeight) and cached on the renderer
// instance - the online-match requirement is that both peers derive the
// identical layout from the identical server-issued mapSeed, so this must
// be a pure function of that seed, never Math.random().

function buildLayout(seed, width, laneY, laneH) {
  const rng = mulberry32(seed);
  const laneBottom = laneY + laneH;
  const skyBottom = laneY + 2;
  const shelfH = 44; // matches the human map's own thin foreground shelf

  const padX = 1200;
  const minX = -padX;
  const maxX = width + padX;
  const totalFieldW = maxX - minX;

  const pyramids = [];
  {
    const count = 10;
    let x = -240;
    for (let i = 0; i < count; i++) {
      const w = 110 + rng() * 210;
      const heightRoll = Math.pow(rng(), 1.7);
      const leftDamp = 0.45 + 0.55 * Math.max(0, Math.min(1, (x + 200) / 620));
      const h = (50 + heightRoll * 250) * leftDamp;
      pyramids.push({
        x,
        w,
        h,
        jitter: Array.from({ length: 6 }, () => (rng() - 0.5) * (14 + rng() * 14)),
        hasVent: rng() > 0.4,
        ventPhase: rng() * Math.PI * 2,
        ventHue: rng() > 0.5 ? 'violet' : 'green',
        veinSeed: Math.floor(rng() * 1e6)
      });
      x += w * (rng() > 0.75 ? 0.15 + rng() * 0.15 : 0.35 + rng() * 0.55);
    }
  }

  function ridgeLayer(minW, maxW, minH, maxH) {
    const arr = [];
    let x = minX - 60;
    while (x < maxX + 60) {
      const w = minW + rng() * (maxW - minW);
      const h = minH + rng() * (maxH - minH);
      arr.push({ x, w, h });
      x += w * (0.6 + rng() * 0.3);
    }
    return arr;
  }
  const farRidge = ridgeLayer(140, 260, 22, 42);
  const nearRidge = ridgeLayer(170, 280, 42, 72);

  const starfield = [];
  const starCount = Math.round(240 * (totalFieldW / width));
  for (let i = 0; i < starCount; i++) {
    starfield.push({
      x: minX + rng() * totalFieldW,
      y: rng() * skyBottom,
      r: rng() * 1.3 + 0.25,
      tint: rng() > 0.82 ? 'violet' : 'white',
      twinklePhase: rng() * Math.PI * 2
    });
  }

  const driftFragments = [];
  const fragmentCount = Math.round(10 * (totalFieldW / width));
  for (let i = 0; i < fragmentCount; i++) {
    driftFragments.push({
      x: minX + rng() * totalFieldW,
      y: 20 + rng() * (skyBottom - 40),
      r: 3 + rng() * 7,
      driftSpeed: 4 + rng() * 8,
      phase: rng() * Math.PI * 2
    });
  }

  const biomassPods = [];
  for (const p of pyramids) {
    const podCount = 1 + Math.floor(rng() * 2);
    for (let i = 0; i < podCount; i++) {
      biomassPods.push({
        x: p.x + rng() * p.w,
        y: laneY - rng() * 6,
        r: 5 + rng() * 9,
        phase: rng() * Math.PI * 2,
        rate: 0.7 + rng() * 0.6,
        hue: rng() > 0.5 ? 'rose' : 'green'
      });
    }
  }

  const backRocks = [];
  {
    const baseCount = 4 + Math.floor(rng() * 4);
    const count = Math.round(baseCount * (totalFieldW / width));
    for (let i = 0; i < count; i++) {
      const x = minX + rng() * totalFieldW;
      const y = laneY + 6 + rng() * 46;
      const w = 9 + rng() * 20;
      const h = 7 + rng() * 12;
      const hasTentacle = rng() < 0.28;
      const vertCount = 6 + Math.floor(rng() * 3);
      const points = [];
      for (let v = 0; v < vertCount; v++) {
        const angle = (v / vertCount) * Math.PI * 2;
        const jitter = 0.55 + rng() * 0.5;
        points.push({ x: Math.cos(angle) * w * jitter, y: Math.sin(angle) * h * jitter });
      }
      backRocks.push({
        x,
        y,
        w,
        h,
        shade: rng(),
        points,
        tentacle: hasTentacle
          ? {
              baseX: x + (rng() - 0.5) * w * 0.4,
              baseY: y - h * 0.6,
              segCount: 4 + Math.floor(rng() * 2),
              segLen: (26 + rng() * 20) / 5,
              baseAngle: -Math.PI / 2 + (rng() - 0.5) * 0.6,
              swayAmp: 0.12 + rng() * 0.18,
              speed: 0.3 + rng() * 0.3,
              phase: rng() * Math.PI * 2
            }
          : null
      });
    }
  }

  const slimeDrips = [];
  for (const p of pyramids) {
    if (!p.hasVent || rng() > 0.6) continue;
    slimeDrips.push({
      x: p.x + p.w * 0.5 + (rng() - 0.5) * 10,
      startY: laneY - p.h * 0.42,
      endY: laneY,
      period: 4.5 + rng() * 4,
      phase: rng() * Math.PI * 2
    });
  }

  const floorVeins = [];
  for (let i = 0; i < 5; i++) {
    const y = laneY + 30 + i * 55;
    if (y > laneBottom - 10) continue;
    const pts = [];
    for (let x = minX - 80; x <= maxX + 80; x += 80) pts.push({ x, y: y + (rng() - 0.5) * 16 });
    floorVeins.push({ pts, alpha: 0.08 + rng() * 0.08 });
  }

  // Fades rubble/patch opacity out near both lane edges (mirrors the grain
  // texture's own double fade, buildLayout below / getGrainFadeCanvas) -
  // without this, the rocks simply stopped appearing at a hard population
  // boundary right at laneY/laneBottom: color was already continuous there
  // (see renderSulStaticBackground's floorGrad fix), but the TEXTURE density
  // itself still had a visible cliff, especially since rocks get bigger
  // (more visually prominent) right up to the depth-scaled cutoff.
  const edgeFadeH = 70;
  function edgeFade(y) {
    return Math.max(0, Math.min(1, Math.min(y - laneY, laneBottom - y) / edgeFadeH));
  }

  const rubble = [];
  const rubbleCount = Math.round(55 * (totalFieldW / width));
  for (let i = 0; i < rubbleCount; i++) {
    const y = laneY + 6 + rng() * (laneH - 12);
    const depthT = (y - laneY) / laneH;
    const scale = 0.32 + depthT * 1.25;
    rubble.push({
      x: minX + rng() * totalFieldW,
      y,
      w: (4 + rng() * 7) * scale,
      h: (3 + rng() * 4) * scale,
      rot: rng() * Math.PI,
      shade: rng(),
      edgeFade: edgeFade(y)
    });
  }
  const patches = [];
  const patchCount = Math.round(8 * (totalFieldW / width));
  for (let i = 0; i < patchCount; i++) {
    const y = laneY + 10 + rng() * (laneH - 20);
    patches.push({ x: minX + rng() * totalFieldW, y, r: 20 + rng() * 40, dark: rng() > 0.5, edgeFade: edgeFade(y) });
  }

  const alienPlants = [];
  {
    const basePlantCount = 2 + Math.floor(rng() * 2);
    const count = Math.round(basePlantCount * (totalFieldW / width));
    for (let i = 0; i < count; i++) {
      const height = 10 + rng() * 14;
      alienPlants.push({
        x: minX + 180 + rng() * (totalFieldW - 360),
        baseY: laneBottom + shelfH * 0.55 + rng() * (shelfH * 0.4 - height),
        height,
        bend: (rng() - 0.5) * 14,
        hue: rng() > 0.5 ? 'violet' : 'green',
        phase: rng() * Math.PI * 2,
        rate: 0.45 + rng() * 0.35
      });
    }
  }

  // Shelf indicator accent: exactly one stylized obsidian rock and one small
  // bioluminescent alien plant nestled beside it, grounding the area beneath
  // the territory indicator rail without overcrowding the shelf.
  const indicatorSide = rng() > 0.5 ? 1 : -1;
  const indicatorRockW = 34 + rng() * 6;
  const indicatorRockH = 15 + rng() * 3;
  const indicatorRockX = width * 0.5 + indicatorSide * (75 + rng() * 25);
  const indicatorRockY = laneBottom + 25;
  const indicatorRock = {
    x: indicatorRockX,
    y: indicatorRockY,
    w: indicatorRockW,
    h: indicatorRockH,
    hue: rng() > 0.5 ? 'violet' : 'green',
    shade: rng()
  };

  const indicatorPlant = {
    x: indicatorRockX + (indicatorSide > 0 ? -indicatorRockW * 0.46 : indicatorRockW * 0.46),
    baseY: indicatorRockY + 2,
    height: 13 + rng() * 4,
    bend: indicatorSide * -5,
    hue: indicatorRock.hue === 'violet' ? 'green' : 'violet',
    phase: rng() * Math.PI * 2,
    rate: 0.55 + rng() * 0.3
  };

  const plasmaTraces = [];
  {
    const baseCount = 5 + Math.floor(rng() * 3);
    const count = Math.round(baseCount * (totalFieldW / width));
    for (let i = 0; i < count; i++) {
      plasmaTraces.push({
        x: minX + rng() * totalFieldW,
        y: laneY + 20 + rng() * (laneH - 40),
        len: 14 + rng() * 22,
        angle: rng() * Math.PI * 2,
        curve: (rng() - 0.5) * 0.8,
        hue: rng() > 0.5 ? 'violet' : 'green'
      });
    }
  }

  const sporesFront = [];
  const sporeFrontCount = Math.round(5 * (totalFieldW / width));
  for (let i = 0; i < sporeFrontCount; i++) {
    sporesFront.push({
      x: minX + rng() * totalFieldW,
      y: laneBottom + 20 + rng() * (shelfH - 40 > 0 ? shelfH - 40 : shelfH),
      r: 2 + rng() * 2.2,
      speedY: -(5 + rng() * 6),
      driftX: (rng() - 0.5) * 6,
      phase: rng() * Math.PI * 2,
      hue: rng() > 0.5 ? 'green' : 'violet'
    });
  }

  // Spore-wind pool: a full-scene-spanning particle field that stays
  // invisible (alpha scaled by windIntensity, see getSulWeatherState) except
  // during the periodic gust - built once here rather than allocated per
  // gust so the wind event costs zero GC. 'back' particles are smaller/
  // dimmer/slower (drawn behind units, renderSulWeatherBackground); 'front'
  // are bigger/brighter/faster (drawn in front of everything,
  // renderSulWeather) - same depth split the human map's own weather uses.
  const windSpores = [];
  const windCount = Math.round(130 * (totalFieldW / width));
  for (let i = 0; i < windCount; i++) {
    const layer = i < Math.floor(windCount * 0.54) ? 'back' : 'front';
    windSpores.push({
      x: minX + rng() * totalFieldW,
      y: rng() * (laneBottom + shelfH),
      r: layer === 'back' ? 1 + rng() * 1.6 : 1.8 + rng() * 2.6,
      layer,
      baseSpeed: layer === 'back' ? 40 + rng() * 40 : 90 + rng() * 90,
      bobAmp: 4 + rng() * 10,
      bobSpeed: 0.6 + rng() * 0.8,
      phase: rng() * Math.PI * 2,
      hue: rng() > 0.5 ? 'green' : 'violet'
    });
  }

  return {
    minX,
    maxX,
    totalFieldW,
    pyramids,
    farRidge,
    nearRidge,
    starfield,
    driftFragments,
    biomassPods,
    backRocks,
    slimeDrips,
    floorVeins,
    floorDetail: { rubble, patches },
    alienPlants,
    indicatorRock,
    indicatorPlant,
    plasmaTraces,
    sporesFront,
    windSpores,
    shelfH
  };
}

function getLayout(renderer, battlefield) {
  const laneY = battlefield.laneY;
  const laneH = battlefield.laneHeight;
  const seed = battlefield.mapSeed || 0;
  if (
    renderer._sulLayout &&
    renderer._sulLayoutKey === `${seed}:${laneY}:${laneH}:${renderer.width}`
  ) {
    return renderer._sulLayout;
  }
  renderer._sulLayout = buildLayout(seed, renderer.width, laneY, laneH);
  renderer._sulLayoutKey = `${seed}:${laneY}:${laneH}:${renderer.width}`;
  return renderer._sulLayout;
}

// --- Grain texture, faded at both lane ends (see sandbox/sul-map for the
// destination-in pitfall this avoids: the mask fillRect must cover the
// WHOLE tile, not just the fade strip, or it erases everything else). -----

let grainTile = null;
function getGrainTile() {
  if (grainTile) return grainTile;
  grainTile = document.createElement('canvas');
  grainTile.width = 96;
  grainTile.height = 96;
  const gctx = grainTile.getContext('2d');
  const img = gctx.createImageData(96, 96);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = 90 + Math.random() * 90;
    img.data[i] = v;
    img.data[i + 1] = v * 0.94;
    img.data[i + 2] = v * 0.97;
    img.data[i + 3] = Math.random() * 60;
  }
  gctx.putImageData(img, 0, 0);
  return grainTile;
}

let grainFadeCache = null;
let grainFadeCacheKey = null;
function getGrainFadeCanvas(width, laneH) {
  const roundedW = Math.max(100, Math.ceil(width / 100) * 100);
  const key = `${roundedW}:${laneH}`;
  if (grainFadeCache && grainFadeCacheKey === key) return grainFadeCache;
  grainFadeCache = document.createElement('canvas');
  grainFadeCache.width = roundedW;
  grainFadeCache.height = laneH;
  const gfx = grainFadeCache.getContext('2d');
  gfx.fillStyle = gfx.createPattern(getGrainTile(), 'repeat');
  gfx.fillRect(0, 0, roundedW, laneH);
  gfx.globalCompositeOperation = 'destination-in';
  const fadeH = Math.min(70, laneH / 3);
  const fade = gfx.createLinearGradient(0, 0, 0, laneH);
  fade.addColorStop(0, 'rgba(0, 0, 0, 0)');
  fade.addColorStop(fadeH / laneH, 'rgba(0, 0, 0, 1)');
  fade.addColorStop(1 - fadeH / laneH, 'rgba(0, 0, 0, 1)');
  fade.addColorStop(1, 'rgba(0, 0, 0, 0)');
  gfx.fillStyle = fade;
  gfx.fillRect(0, 0, roundedW, laneH);
  gfx.globalCompositeOperation = 'source-over';
  grainFadeCacheKey = key;
  return grainFadeCache;
}

// --- Cached spore-glow LUT sprite (renderSulDynamicForeground's sporesFront) --
// One small radial-gradient sprite per hue, built once and reused via
// ctx.drawImage() (scaled to each spore's current radius) instead of a fresh
// ctx.createRadialGradient() per spore per frame (~12-13 gradients/frame) -
// same LUT idiom as getGrainFadeCanvas() above. Center alpha is baked at the
// spore glow's original 0.6; per-spore/per-frame flicker is then applied via
// ctx.globalAlpha, exactly reproducing the original "0.6 * flicker" center
// alpha (0 * flicker = 0 at the faded edge either way). Sized 64x64 so every
// on-screen spore (max diameter ~27px, since spore r tops out at 3.2*4.2)
// only ever downscales the sprite, never magnifies it, so no blur/blockiness
// versus the native per-frame gradient it replaces.
const SPORE_GLOW_LUT_SIZE = 64;
let sporeGlowLUTs = null;
function getSporeGlowLUT(hue) {
  if (!sporeGlowLUTs) sporeGlowLUTs = {};
  if (sporeGlowLUTs[hue]) return sporeGlowLUTs[hue];
  const c = document.createElement('canvas');
  c.width = SPORE_GLOW_LUT_SIZE;
  c.height = SPORE_GLOW_LUT_SIZE;
  const gctx = c.getContext('2d');
  const cx = SPORE_GLOW_LUT_SIZE / 2;
  const color = hue === 'green' ? '101, 163, 13' : '217, 70, 239';
  const grad = gctx.createRadialGradient(cx, cx, 0, cx, cx, cx);
  grad.addColorStop(0, `rgba(${color}, 0.6)`);
  grad.addColorStop(1, `rgba(${color}, 0)`);
  gctx.fillStyle = grad;
  gctx.fillRect(0, 0, SPORE_GLOW_LUT_SIZE, SPORE_GLOW_LUT_SIZE);
  sporeGlowLUTs[hue] = c;
  return sporeGlowLUTs[hue];
}

let alienPlantGlowLUTs = null;
function getAlienPlantGlowLUT(hue) {
  if (!alienPlantGlowLUTs) alienPlantGlowLUTs = {};
  if (alienPlantGlowLUTs[hue]) return alienPlantGlowLUTs[hue];
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = SPORE_GLOW_LUT_SIZE;
  c.height = SPORE_GLOW_LUT_SIZE;
  const gctx = c.getContext('2d');
  const cx = SPORE_GLOW_LUT_SIZE / 2;
  const rgb = hue === 'green' ? '132, 204, 22' : '217, 70, 239';
  const grad = gctx.createRadialGradient(cx, cx, 0, cx, cx, cx);
  grad.addColorStop(0, `rgba(${rgb}, 0.6)`);
  grad.addColorStop(1, `rgba(${rgb}, 0)`);
  gctx.fillStyle = grad;
  gctx.fillRect(0, 0, SPORE_GLOW_LUT_SIZE, SPORE_GLOW_LUT_SIZE);
  alienPlantGlowLUTs[hue] = c;
  return alienPlantGlowLUTs[hue];
}

// --- Small shared drawing helpers ----------------------------------------

const MOON_CRATERS = [
  { dx: -0.35, dy: -0.15, r: 0.24 },
  { dx: 0.28, dy: 0.32, r: 0.18 },
  { dx: 0.42, dy: -0.28, r: 0.13 },
  { dx: -0.08, dy: 0.12, r: 0.1 },
  { dx: 0.02, dy: -0.5, r: 0.09 }
];

// Moon-flare light shafts (renderSulDynamicBackground) - 4 fixed cones, hoisted
// module-level (Object.freeze, Paradigm 1) instead of allocated fresh every frame.
const MOON_RAYS = Object.freeze([
  { off: -132, wTop: 8, wBot: 66, alphaMul: 0.7 },
  { off: -95, wTop: 12, wBot: 92, alphaMul: 1 },
  { off: -55, wTop: 10, wBot: 78, alphaMul: 0.85 },
  { off: -20, wTop: 7, wBot: 54, alphaMul: 0.6 }
]);

function drawMoonCrater(ctx, cx, cy, cr) {
  const shadow = ctx.createRadialGradient(cx + cr * 0.28, cy + cr * 0.28, cr * 0.1, cx, cy, cr);
  shadow.addColorStop(0, 'rgba(0, 0, 0, 0.4)');
  shadow.addColorStop(0.65, 'rgba(0, 0, 0, 0.22)');
  shadow.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = shadow;
  ctx.beginPath();
  ctx.arc(cx, cy, cr, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
  ctx.lineWidth = Math.max(1, cr * 0.16);
  ctx.beginPath();
  ctx.arc(cx, cy, cr * 0.9, Math.PI * 0.75, Math.PI * 1.55);
  ctx.stroke();
}

const SAT_ORBIT = { rx: 78, ry: 22, speed: 0.45, tilt: -0.18, radius: 5 };
function satellitePosition(moonX, moonY, t) {
  const angle = t * SAT_ORBIT.speed;
  const ex = Math.cos(angle) * SAT_ORBIT.rx;
  const ey = Math.sin(angle) * SAT_ORBIT.ry;
  const cosT = Math.cos(SAT_ORBIT.tilt);
  const sinT = Math.sin(SAT_ORBIT.tilt);
  return {
    x: moonX + ex * cosT - ey * sinT,
    y: moonY + ex * sinT + ey * cosT,
    behind: Math.sin(angle) < 0
  };
}
let _satSpriteCanvas = null;
function getSatSpriteCanvas() {
  if (!_satSpriteCanvas) {
    if (typeof document === 'undefined') return null;
    _satSpriteCanvas = document.createElement('canvas');
    _satSpriteCanvas.width = 16;
    _satSpriteCanvas.height = 16;
    const sctx = _satSpriteCanvas.getContext('2d');
    const r = SAT_ORBIT.radius;
    const body = sctx.createRadialGradient(8 - r * 0.3, 8 - r * 0.3, r * 0.1, 8, 8, r);
    body.addColorStop(0, 'rgba(214, 210, 214, 0.95)');
    body.addColorStop(0.6, 'rgba(122, 118, 126, 0.9)');
    body.addColorStop(1, 'rgba(40, 38, 44, 0.95)');
    sctx.fillStyle = body;
    sctx.beginPath();
    sctx.arc(8, 8, r, 0, Math.PI * 2);
    sctx.fill();
  }
  return _satSpriteCanvas;
}

function drawSatellite(ctx, x, y) {
  const sprite = getSatSpriteCanvas();
  if (sprite) {
    ctx.drawImage(sprite, Math.round(x - 8), Math.round(y - 8));
    return;
  }
  const r = SAT_ORBIT.radius;
  const body = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, r * 0.1, x, y, r);
  body.addColorStop(0, 'rgba(214, 210, 214, 0.95)');
  body.addColorStop(0.6, 'rgba(122, 118, 126, 0.9)');
  body.addColorStop(1, 'rgba(40, 38, 44, 0.95)');
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

const _tentaclePtsScratch = [];
for (let i = 0; i < 32; i++) {
  _tentaclePtsScratch.push({ x: 0, y: 0 });
}

let _tentacleGlowCanvas = null;
function getTentacleGlowCanvas() {
  if (!_tentacleGlowCanvas) {
    if (typeof document === 'undefined') return null;
    _tentacleGlowCanvas = document.createElement('canvas');
    _tentacleGlowCanvas.width = 32;
    _tentacleGlowCanvas.height = 32;
    const gctx = _tentacleGlowCanvas.getContext('2d');
    const glow = gctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    glow.addColorStop(0, 'rgba(200, 180, 230, 0.55)');
    glow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    gctx.fillStyle = glow;
    gctx.fillRect(0, 0, 32, 32);
  }
  return _tentacleGlowCanvas;
}

// windBoost multiplies the sway amplitude (1 = normal, up to ~3.5 during a
// spore-wind gust peak - see getSulWeatherState) and adds a steady lean in
// the wind's direction so it reads as being pushed, not just wobbling more.
function tentaclePoints(tentacle, t, windBoost = 1) {
  _tentaclePtsScratch[0].x = tentacle.baseX;
  _tentaclePtsScratch[0].y = tentacle.baseY;
  let x = tentacle.baseX;
  let y = tentacle.baseY;
  const lean = (windBoost - 1) * 0.18;
  const count = tentacle.segCount;
  for (let i = 1; i <= count; i++) {
    const frac = i / count;
    const sway = Math.sin(t * tentacle.speed + tentacle.phase + i * 0.7) * tentacle.swayAmp * windBoost * frac;
    const angle = tentacle.baseAngle + sway + lean * frac;
    x += Math.cos(angle) * tentacle.segLen;
    y += Math.sin(angle) * tentacle.segLen;
    if (i >= _tentaclePtsScratch.length) {
      _tentaclePtsScratch.push({ x, y });
    } else {
      _tentaclePtsScratch[i].x = x;
      _tentaclePtsScratch[i].y = y;
    }
  }
  _tentaclePtsScratch.length = count + 1;
  return _tentaclePtsScratch;
}

function drawTentacle(ctx, tentacle, t, { color, glowColor, baseWidth }, windBoost = 1) {
  const pts = tentaclePoints(tentacle, t, windBoost);
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  for (let i = 0; i < pts.length - 1; i++) {
    const widthFrac = 1 - i / (pts.length - 1);
    ctx.lineWidth = Math.max(0.6, baseWidth * widthFrac);
    ctx.beginPath();
    ctx.moveTo(pts[i].x, pts[i].y);
    ctx.lineTo(pts[i + 1].x, pts[i + 1].y);
    ctx.stroke();
  }
  const tip = pts[pts.length - 1];
  const glowR = baseWidth * 2.2;
  const tGlow = getTentacleGlowCanvas();
  if (tGlow) {
    ctx.drawImage(tGlow, tip.x - glowR, tip.y - glowR, glowR * 2, glowR * 2);
  } else {
    const glow = ctx.createRadialGradient(tip.x, tip.y, 0, tip.x, tip.y, glowR);
    glow.addColorStop(0, glowColor);
    glow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(tip.x, tip.y, glowR, 0, Math.PI * 2);
    ctx.fill();
  }
}

// Shared by drawPyramid() (structural fill/veins, baked once statically) and
// drawPyramidVentGlow() (the animated vent light, drawn every frame in the
// dynamic pass - see the item 5 fix below) so both clip to the exact same
// jittered silhouette rather than risking the glow bleeding past the pyramid
// edge if the two paths ever drifted out of sync.
function buildPyramidPath(ctx, p, laneY) {
  const baseY = laneY;
  const apexX = p.x + p.w / 2;
  const apexY = baseY - p.h;
  const steps = p.jitter.length - 1;

  ctx.beginPath();
  ctx.moveTo(apexX + p.jitter[0], apexY);
  for (let i = 1; i <= steps; i++) {
    const fx = apexX + (p.x + p.w - apexX) * (i / steps);
    const fy = apexY + (baseY - apexY) * (i / steps);
    ctx.lineTo(fx + p.jitter[i], fy);
  }
  ctx.lineTo(p.x + p.w, baseY);
  ctx.lineTo(p.x, baseY);
  for (let i = steps; i >= 1; i--) {
    const fx = p.x + (apexX - p.x) * (i / steps);
    const fy = apexY + (baseY - apexY) * (i / steps);
    ctx.lineTo(fx - p.jitter[i] * 0.6, fy);
  }
  ctx.closePath();
}

// Static structural body only (fill, facet shading, apex-to-base highlight
// line, mineral veins). The vent glow used to be pulsed here too, but since
// this is only ever invoked from renderSulStaticBackground (baked once at
// t=0), that "pulse" was actually frozen forever - see drawPyramidVentGlow,
// which now owns the animated part and runs every frame from
// renderSulDynamicBackground instead.
function drawPyramid(ctx, p, laneY, quality) {
  const baseY = laneY;
  const apexX = p.x + p.w / 2;
  const apexY = baseY - p.h;

  buildPyramidPath(ctx, p, laneY);

  const grad = ctx.createLinearGradient(p.x, apexY, p.x, baseY);
  grad.addColorStop(0, 'rgba(74, 65, 82, 0.96)');
  grad.addColorStop(0.55, 'rgba(42, 36, 48, 0.96)');
  grad.addColorStop(1, 'rgba(17, 13, 21, 0.97)');
  ctx.fillStyle = grad;
  ctx.fill();

  const facet = ctx.createLinearGradient(p.x, 0, p.x + p.w, 0);
  facet.addColorStop(0, 'rgba(0, 0, 0, 0.32)');
  facet.addColorStop(0.5, 'rgba(0, 0, 0, 0)');
  facet.addColorStop(0.78, 'rgba(255, 255, 255, 0.05)');
  facet.addColorStop(1, 'rgba(255, 255, 255, 0.17)');
  ctx.fillStyle = facet;
  ctx.fill();

  ctx.save();
  ctx.clip();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.14)';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(apexX + p.jitter[0], apexY);
  ctx.lineTo(p.x + p.w * 0.62, baseY);
  ctx.stroke();

  if (quality !== 'low') {
    const veinRng = mulberry32(p.veinSeed);
    const veinCount = 2 + Math.floor(veinRng() * 3);
    for (let v = 0; v < veinCount; v++) {
      const isGreen = veinRng() > 0.55;
      ctx.strokeStyle = isGreen ? 'rgba(101, 163, 13, 0.45)' : 'rgba(217, 70, 239, 0.4)';
      ctx.lineWidth = 1.2;
      let vx = p.x + p.w * (0.15 + veinRng() * 0.7);
      let vy = baseY;
      ctx.beginPath();
      ctx.moveTo(vx, vy);
      const segs = 5;
      for (let s = 0; s < segs; s++) {
        vy -= p.h / segs;
        vx += (veinRng() - 0.5) * 18;
        ctx.lineTo(vx, vy);
      }
      ctx.stroke();
    }
  }

  ctx.restore();
}

// Animated vent-glow pulse, split out of drawPyramid() (item 5 fix): that
// function is only ever called once, at t=0, from renderSulStaticBackground,
// so a pulse computed there is baked forever and never actually animates in
// game. This draws just the glow, clipped to the exact same pyramid
// silhouette (buildPyramidPath) so it never bleeds past the structural body,
// which stays fully static/baked as before. Called every frame from
// renderSulDynamicBackground for every pyramid; bails out immediately for
// the ~40% that have no vent at all (layout.pyramids isn't pre-filtered, so
// this keeps that filtering out of the hot loop's own bookkeeping).
//
let _sulVentGlowGreen = null;
let _sulVentGlowViolet = null;

function getSulVentGlowCanvas(hue) {
  if (typeof document === 'undefined') return null;
  if (hue === 'green') {
    if (!_sulVentGlowGreen) {
      _sulVentGlowGreen = document.createElement('canvas');
      _sulVentGlowGreen.width = 32;
      _sulVentGlowGreen.height = 32;
      const gctx = _sulVentGlowGreen.getContext('2d');
      const glow = gctx.createRadialGradient(16, 16, 1, 16, 16, 16);
      glow.addColorStop(0, 'rgba(101, 163, 13, 0.8)');
      glow.addColorStop(0.6, 'rgba(63, 98, 18, 0.32)');
      glow.addColorStop(1, 'rgba(101, 163, 13, 0)');
      gctx.fillStyle = glow;
      gctx.beginPath();
      gctx.arc(16, 16, 16, 0, Math.PI * 2);
      gctx.fill();
    }
    return _sulVentGlowGreen;
  } else {
    if (!_sulVentGlowViolet) {
      _sulVentGlowViolet = document.createElement('canvas');
      _sulVentGlowViolet.width = 32;
      _sulVentGlowViolet.height = 32;
      const gctx = _sulVentGlowViolet.getContext('2d');
      const glow = gctx.createRadialGradient(16, 16, 1, 16, 16, 16);
      glow.addColorStop(0, 'rgba(217, 70, 239, 0.8)');
      glow.addColorStop(0.6, 'rgba(147, 51, 234, 0.32)');
      glow.addColorStop(1, 'rgba(217, 70, 239, 0)');
      gctx.fillStyle = glow;
      gctx.beginPath();
      gctx.arc(16, 16, 16, 0, Math.PI * 2);
      gctx.fill();
    }
    return _sulVentGlowViolet;
  }
}

// Animated vent-glow pulse, split out of drawPyramid() (item 5 fix): that
// function is only ever called once, at t=0, from renderSulStaticBackground,
// so a pulse computed there is baked forever and never actually animates in
// game.
// Zero-allocation cached sprite: avoids per-frame createRadialGradient and
// eliminates pipeline-stalling ctx.clip() calls across all pyramids.
function drawPyramidVentGlow(ctx, p, laneY, t, quality) {
  if (!p.hasVent) return;
  const pulse = quality === 'low' ? 0.7 : 0.55 + 0.45 * Math.sin(t * 1.8 + p.ventPhase);
  const vx = p.x + p.w * 0.5;
  const vy = laneY - p.h * 0.42;
  const sprite = getSulVentGlowCanvas(p.ventHue);
  if (sprite) {
    const prevAlpha = ctx.globalAlpha;
    ctx.globalAlpha = prevAlpha * pulse;
    ctx.drawImage(sprite, vx - 16, vy - 16);
    ctx.globalAlpha = prevAlpha;
  }
}

// KNOWN ISSUE (item 5, left unfixed - see docs/PERFORMANCE_AND_STABILITY_AUDIT.md):
// drawBiomassPod()'s "breathe" pulse has the exact same frozen-at-t=0 bug as
// the pyramid vent glow above (only ever called from renderSulStaticBackground).
// Unlike the vent glow, "breathe" isn't a separate light layer drawn on top of
// a static body - it directly scales the pod's own radius (`r = pod.r *
// breathe`), which every fill/gradient below is derived from, so the "static
// structural shape" and the "pulsing" are the same draw call, not separable
// without either (a) re-deriving a byte-identical baseline body to subtract/
// re-add per frame, or (b) moving the whole pod (body + gradients) into the
// dynamic pass, which would reintroduce a per-frame createRadialGradient cost
// for every pod (~15 on a typical map) - i.e. undoing part of the LUT-caching
// work this same pass just did for sporesFront. Per this task's own guidance
// ("acceptable to leave as a documented known issue instead of risking a
// visual regression"), this was left as-is rather than risk either outcome.
function drawBiomassPod(ctx, pod, t, quality) {
  const breathe = quality === 'low' ? 1 : 0.85 + 0.15 * Math.sin(t * pod.rate + pod.phase);
  const r = pod.r * breathe;
  const rose = pod.hue === 'rose';
  const grad = ctx.createRadialGradient(pod.x - r * 0.3, pod.y - r * 0.3, r * 0.1, pod.x, pod.y, r);
  if (rose) {
    grad.addColorStop(0, 'rgba(190, 110, 130, 0.85)');
    grad.addColorStop(0.6, 'rgba(120, 55, 75, 0.85)');
    grad.addColorStop(1, 'rgba(50, 20, 32, 0.9)');
  } else {
    grad.addColorStop(0, 'rgba(120, 150, 90, 0.8)');
    grad.addColorStop(0.6, 'rgba(70, 95, 50, 0.82)');
    grad.addColorStop(1, 'rgba(30, 42, 22, 0.9)');
  }
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.ellipse(pod.x, pod.y, r, r * 0.75, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(pod.x + r * 0.7, pod.y + r * 0.15, r * 0.45, r * 0.35, 0, 0, Math.PI * 2);
  ctx.fill();
}

function drawBackRock(ctx, r) {
  const base = 46 + r.shade * 34;
  const grad = ctx.createRadialGradient(r.x - r.w * 0.25, r.y - r.h * 0.35, 1, r.x, r.y, Math.max(r.w, r.h));
  grad.addColorStop(0, `rgba(${base + 30}, ${base + 24}, ${base + 30}, 0.97)`);
  grad.addColorStop(1, `rgba(${Math.max(0, base - 22)}, ${Math.max(0, base - 26)}, ${Math.max(0, base - 20)}, 0.97)`);
  ctx.fillStyle = grad;
  ctx.beginPath();
  r.points.forEach((p, i) => {
    const px = r.x + p.x;
    const py = r.y + p.y;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  });
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = 'rgba(210, 200, 215, 0.3)';
  ctx.lineWidth = 1;
  ctx.stroke();
}

function drawShelfRock(ctx, r) {
  const halfW = r.w * 0.5;
  const halfH = r.h * 0.5;

  ctx.save();
  // 1. Soft contact shadow on the membrane floor
  const shadowGrad = ctx.createRadialGradient(r.x, r.y + 2, 2, r.x, r.y + 2, halfW * 1.25);
  shadowGrad.addColorStop(0, 'rgba(3, 2, 6, 0.65)');
  shadowGrad.addColorStop(1, 'rgba(3, 2, 6, 0)');
  ctx.fillStyle = shadowGrad;
  ctx.beginPath();
  ctx.ellipse(r.x, r.y + 2, halfW * 1.2, halfH * 0.55, 0, 0, Math.PI * 2);
  ctx.fill();

  // 2. Chiseled faceted obsidian boulder
  const pts = [
    { x: r.x - halfW, y: r.y + halfH * 0.35 },
    { x: r.x - halfW * 0.85, y: r.y - halfH * 0.3 },
    { x: r.x - halfW * 0.45, y: r.y - halfH * 0.85 },
    { x: r.x - halfW * 0.05, y: r.y - halfH * 1.0 },
    { x: r.x + halfW * 0.45, y: r.y - halfH * 0.7 },
    { x: r.x + halfW * 0.88, y: r.y - halfH * 0.15 },
    { x: r.x + halfW, y: r.y + halfH * 0.35 }
  ];

  const bodyGrad = ctx.createLinearGradient(r.x - halfW * 0.3, r.y - halfH, r.x + halfW * 0.2, r.y + halfH);
  bodyGrad.addColorStop(0, '#36293d');
  bodyGrad.addColorStop(0.45, '#221929');
  bodyGrad.addColorStop(1, '#0e0914');
  ctx.fillStyle = bodyGrad;
  ctx.beginPath();
  pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath();
  ctx.fill();

  // 3. Shaded right facet (gives 3D relief)
  ctx.fillStyle = 'rgba(10, 6, 14, 0.42)';
  ctx.beginPath();
  ctx.moveTo(r.x - halfW * 0.05, r.y - halfH * 1.0);
  ctx.lineTo(r.x + halfW * 0.45, r.y - halfH * 0.7);
  ctx.lineTo(r.x + halfW * 0.88, r.y - halfH * 0.15);
  ctx.lineTo(r.x + halfW, r.y + halfH * 0.35);
  ctx.lineTo(r.x - halfW * 0.05, r.y + halfH * 0.35);
  ctx.closePath();
  ctx.fill();

  // 4. Specular top-rim highlight
  ctx.strokeStyle = 'rgba(215, 200, 230, 0.28)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(r.x - halfW * 0.85, r.y - halfH * 0.3);
  ctx.lineTo(r.x - halfW * 0.45, r.y - halfH * 0.85);
  ctx.lineTo(r.x - halfW * 0.05, r.y - halfH * 1.0);
  ctx.lineTo(r.x + halfW * 0.45, r.y - halfH * 0.7);
  ctx.stroke();

  // 5. Delicate glowing mineral vein
  const rgb = r.hue === 'green' ? '132, 204, 22' : '217, 70, 239';
  ctx.strokeStyle = `rgba(${rgb}, 0.65)`;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(r.x - halfW * 0.25, r.y - halfH * 0.45);
  ctx.lineTo(r.x - halfW * 0.05, r.y - halfH * 0.1);
  ctx.lineTo(r.x + halfW * 0.2, r.y + halfH * 0.15);
  ctx.stroke();

  const veinGlow = ctx.createRadialGradient(r.x - halfW * 0.05, r.y - halfH * 0.1, 0, r.x - halfW * 0.05, r.y - halfH * 0.1, 8);
  veinGlow.addColorStop(0, `rgba(${rgb}, 0.22)`);
  veinGlow.addColorStop(1, `rgba(${rgb}, 0)`);
  ctx.fillStyle = veinGlow;
  ctx.beginPath();
  ctx.arc(r.x - halfW * 0.05, r.y - halfH * 0.1, 8, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

// swayEnabled (item 2, medium tier): lets the caller freeze a fraction of
// the plants into a static pose (no sway, no bulb breathe) on 'medium' to
// cut per-plant trig work, without touching 'low' (already fully static)
// or 'high'/'ultra' (always fully animated - swayEnabled defaults true).
function drawAlienPlant(ctx, p, t, quality, windBoost = 1, swayEnabled = true) {
  const animated = quality !== 'low' && swayEnabled;
  const sway = animated ? Math.sin(t * p.rate + p.phase) * 4 * windBoost + (windBoost - 1) * 5 : 0;
  const tipX = p.x + p.bend + sway;
  const tipY = p.baseY - p.height;
  const midX = p.x + p.bend * 0.5 + sway * 0.5;
  const midY = p.baseY - p.height * 0.55;
  const rgb = p.hue === 'green' ? '132, 204, 22' : '217, 70, 239';

  ctx.strokeStyle = p.hue === 'green' ? 'rgba(74, 124, 60, 0.9)' : 'rgba(107, 66, 130, 0.9)';
  ctx.lineWidth = 4;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(p.x, p.baseY);
  ctx.quadraticCurveTo(midX, midY, tipX, tipY);
  ctx.stroke();

  ctx.strokeStyle = `rgba(${rgb}, 0.5)`;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(p.x, p.baseY);
  ctx.quadraticCurveTo(midX, midY, tipX, tipY);
  ctx.stroke();

  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.quadraticCurveTo(tipX + side * 10, tipY - 4, tipX + side * 14, tipY + 6);
    ctx.stroke();
  }

  const breathe = animated ? 0.85 + 0.15 * Math.sin(t * p.rate * 1.3 + p.phase) : 1;
  const bulbR = 7 * breathe;
  const glowR = bulbR * 2.2;
  const plantLut = getAlienPlantGlowLUT(p.hue);
  if (plantLut && ctx.drawImage) {
    ctx.drawImage(plantLut, tipX - glowR, tipY - glowR, glowR * 2, glowR * 2);
  } else {
    const glow = ctx.createRadialGradient(tipX, tipY, 0, tipX, tipY, glowR);
    glow.addColorStop(0, `rgba(${rgb}, 0.6)`);
    glow.addColorStop(1, `rgba(${rgb}, 0)`);
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(tipX, tipY, glowR, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = `rgba(${rgb}, 0.85)`;
  ctx.beginPath();
  ctx.arc(tipX, tipY, bulbR * 0.55, 0, Math.PI * 2);
  ctx.fill();
}

// --- Plasma-arc lightning ---------------------------------------------
// Jagged bolt via midpoint displacement (classic lightning-bolt
// construction): start with a straight line, then repeatedly split each
// segment at its midpoint and nudge it sideways, halving the nudge each
// pass so later passes add fine jitter rather than large kinks.
function buildBoltPath(rng, x1, y1, x2, y2, passes, displace) {
  let points = [
    { x: x1, y: y1 },
    { x: x2, y: y2 }
  ];
  for (let pass = 0; pass < passes; pass++) {
    const next = [points[0]];
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const mx = (p1.x + p2.x) / 2;
      const my = (p1.y + p2.y) / 2;
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len;
      const ny = dx / len;
      const amt = (rng() - 0.5) * displace;
      next.push({ x: mx + nx * amt, y: my + ny * amt });
      next.push(p2);
    }
    points = next;
    displace *= 0.55;
  }
  return points;
}

function strokeBoltPath(ctx, points) {
  ctx.beginPath();
  points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.stroke();
}

// Rare (~every LIGHTNING_CYCLE seconds), brief arc jumping between two
// vent-bearing pyramids - the "living power source" reading, rather than a
// generic storm bolt from clouds that don't exist on this world. Returns
// the flash strength (0-1) for the caller to layer a scene-wide brighten on
// top, same idea as real lightning lighting up more than just the bolt.
const LIGHTNING_CYCLE = 22;
const LIGHTNING_ACTIVE = 0.4;
function drawPlasmaLightning(ctx, layout, laneY, t, offsetHolder) {
  const ventPyramids = layout.pyramids.filter((p) => p.hasVent);
  if (ventPyramids.length < 2) return 0;
  if (offsetHolder.value === undefined) offsetHolder.value = Math.random() * LIGHTNING_CYCLE;
  const cyclePos = (t + offsetHolder.value) % LIGHTNING_CYCLE;
  if (cyclePos > LIGHTNING_ACTIVE) return 0;

  const cycleIndex = Math.floor((t + offsetHolder.value) / LIGHTNING_CYCLE);
  const rng = mulberry32(cycleIndex * 104729 + 7);
  const sorted = [...ventPyramids].sort((a, b) => a.x - b.x);
  const i = Math.floor(rng() * (sorted.length - 1));
  const a = sorted[i];
  const b = sorted[i + 1];
  const ax = a.x + a.w * 0.5;
  const ay = laneY - a.h * 0.42;
  const bx = b.x + b.w * 0.5;
  const by = laneY - b.h * 0.42;

  const progress = cyclePos / LIGHTNING_ACTIVE;
  // Fast attack, slower decay - the same envelope shape as the moon flare.
  const flash = progress < 0.2 ? progress / 0.2 : (1 - progress) / 0.8;
  const hue = rng() > 0.5 ? '217, 70, 239' : '101, 163, 13';
  const points = buildBoltPath(rng, ax, ay, bx, by, 4, 26);

  ctx.save();
  ctx.globalCompositeOperation = 'screen';
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = `rgba(${hue}, ${0.55 * flash})`;
  ctx.lineWidth = 7;
  strokeBoltPath(ctx, points);
  ctx.strokeStyle = `rgba(${hue}, ${0.75 * flash})`;
  ctx.lineWidth = 3;
  strokeBoltPath(ctx, points);
  ctx.strokeStyle = `rgba(255, 255, 255, ${0.9 * flash})`;
  ctx.lineWidth = 1.3;
  strokeBoltPath(ctx, points);
  ctx.restore();

  return flash;
}

// Wind-spore particle, shared by the background (behind units) and
// foreground (in front of units) weather layers - only the pool slice and
// depth-appropriate size/speed differ (see buildLayout's windSpores).
function drawWindSpore(ctx, s, t, windIntensity, minXOrWidth, optTotalFieldW) {
  if (windIntensity <= 0.01) return;
  const speed = s.baseSpeed * windIntensity;
  const minX = optTotalFieldW !== undefined ? minXOrWidth : -30;
  const totalW = optTotalFieldW !== undefined ? optTotalFieldW : (minXOrWidth + 60);
  const x = minX + (((s.x - minX + t * speed) % totalW) + totalW) % totalW;
  const y = s.y + Math.sin(t * s.bobSpeed + s.phase) * s.bobAmp;
  const alpha = windIntensity * (0.55 + 0.35 * Math.sin(t * 2 + s.phase));
  const color = s.hue === 'green' ? '132, 204, 22' : '217, 70, 239';
  ctx.fillStyle = `rgba(${color}, ${Math.max(0, alpha)})`;
  ctx.beginPath();
  ctx.ellipse(x, y, s.r, s.r * 0.6, Math.atan2(0, speed), 0, Math.PI * 2);
  ctx.fill();
}

// Zero-allocation caches for dynamic background effects
let _sulMoonFlareCanvas = null;
function getSulMoonFlareCanvas() {
  if (typeof document === 'undefined') return null;
  if (!_sulMoonFlareCanvas) {
    _sulMoonFlareCanvas = document.createElement('canvas');
    const r = Math.ceil(46 * 2.2);
    _sulMoonFlareCanvas.width = r * 2;
    _sulMoonFlareCanvas.height = r * 2;
    const fctx = _sulMoonFlareCanvas.getContext('2d');
    const flareGlow = fctx.createRadialGradient(r, r, 46 * 0.3, r, r, r);
    flareGlow.addColorStop(0, 'rgba(226, 232, 220, 0.25)');
    flareGlow.addColorStop(1, 'rgba(226, 232, 220, 0)');
    fctx.fillStyle = flareGlow;
    fctx.beginPath();
    fctx.arc(r, r, r, 0, Math.PI * 2);
    fctx.fill();
  }
  return _sulMoonFlareCanvas;
}

const _moonRayGrads = new Map();
function getMoonRayGrad(ctx, moonX, moonY, botX, targetY) {
  const key = `${moonX}_${moonY}_${botX}_${targetY}`;
  let grad = _moonRayGrads.get(key);
  if (!grad) {
    grad = ctx.createLinearGradient(moonX, moonY, botX, targetY);
    grad.addColorStop(0, 'rgba(215, 228, 210, 0.065)');
    grad.addColorStop(0.5, 'rgba(190, 210, 190, 0.032)');
    grad.addColorStop(1, 'rgba(190, 210, 190, 0)');
    _moonRayGrads.set(key, grad);
  }
  return grad;
}

let _meteorGlowCanvas = null;
function getMeteorGlowCanvas() {
  if (typeof document === 'undefined') return null;
  if (!_meteorGlowCanvas) {
    _meteorGlowCanvas = document.createElement('canvas');
    _meteorGlowCanvas.width = 16;
    _meteorGlowCanvas.height = 16;
    const mctx = _meteorGlowCanvas.getContext('2d');
    const glow = mctx.createRadialGradient(8, 8, 0, 8, 8, 8);
    glow.addColorStop(0, 'rgba(220, 240, 230, 0.9)');
    glow.addColorStop(1, 'rgba(220, 240, 230, 0)');
    mctx.fillStyle = glow;
    mctx.beginPath();
    mctx.arc(8, 8, 8, 0, Math.PI * 2);
    mctx.fill();
  }
  return _meteorGlowCanvas;
}

let _slimeDripTrailGrad = null;
function getSlimeDripTrailGrad(ctx) {
  if (!_slimeDripTrailGrad) {
    _slimeDripTrailGrad = ctx.createLinearGradient(0, 0, 0, 18);
    _slimeDripTrailGrad.addColorStop(0, 'rgba(101, 163, 13, 0)');
    _slimeDripTrailGrad.addColorStop(1, 'rgba(132, 204, 22, 0.5)');
  }
  return _slimeDripTrailGrad;
}

let _sulWispGlowCanvas = null;
function getSulWispGlowCanvas() {
  if (typeof document === 'undefined') return null;
  if (!_sulWispGlowCanvas) {
    _sulWispGlowCanvas = document.createElement('canvas');
    _sulWispGlowCanvas.width = 16;
    _sulWispGlowCanvas.height = 16;
    const wctx = _sulWispGlowCanvas.getContext('2d');
    const glow = wctx.createRadialGradient(8, 8, 0, 8, 8, 8);
    glow.addColorStop(0, 'rgba(217, 70, 239, 0.55)');
    glow.addColorStop(1, 'rgba(217, 70, 239, 0)');
    wctx.fillStyle = glow;
    wctx.beginPath();
    wctx.arc(8, 8, 8, 0, Math.PI * 2);
    wctx.fill();
  }
  return _sulWispGlowCanvas;
}

let _sulBottomCritterBodyGrad = null;
function getSulBottomCritterBodyGrad(ctx) {
  if (!_sulBottomCritterBodyGrad) {
    _sulBottomCritterBodyGrad = ctx.createRadialGradient(-4, -6, 2, 0, 0, 20);
    _sulBottomCritterBodyGrad.addColorStop(0, 'rgba(90, 80, 96, 0.95)');
    _sulBottomCritterBodyGrad.addColorStop(0.6, 'rgba(52, 46, 60, 0.95)');
    _sulBottomCritterBodyGrad.addColorStop(1, 'rgba(20, 17, 24, 0.96)');
  }
  return _sulBottomCritterBodyGrad;
}

export const sulBackgroundMixin = {
  renderSulStaticBackground(ctx, battlefield, customBounds = null) {
    const t = 0;
    const laneY = battlefield.laneY;
    const laneH = battlefield.laneHeight;
    const skyBottom = laneY + 2;
    const { visibleTop, visibleBottom, visibleLeft, visibleRight } = resolveBounds(this, customBounds);
    const bgWidth = visibleRight - visibleLeft;
    const layout = getLayout(this, battlefield);

    ctx.fillStyle = '#020103';
    ctx.fillRect(visibleLeft, visibleTop, bgWidth, visibleBottom - visibleTop);

    const sky = ctx.createLinearGradient(0, visibleTop, 0, skyBottom);
    sky.addColorStop(0, '#020103');
    sky.addColorStop(0.35, '#0a0614');
    sky.addColorStop(0.7, '#1a0f27');
    sky.addColorStop(1, '#1f2410');
    ctx.fillStyle = sky;
    ctx.fillRect(visibleLeft, visibleTop, bgWidth, skyBottom - visibleTop);

    for (const s of layout.starfield) {
      ctx.fillStyle = s.tint === 'violet' ? 'rgba(216, 180, 254, 0.4)' : 'rgba(226, 232, 240, 0.55)';
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();
    }

    const violet = ctx.createRadialGradient(560, 130, 20, 560, 130, 360);
    violet.addColorStop(0, 'rgba(217, 70, 239, 0.1)');
    violet.addColorStop(0.45, 'rgba(147, 51, 234, 0.055)');
    violet.addColorStop(0.8, 'rgba(76, 29, 149, 0.02)');
    violet.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = violet;
    ctx.fillRect(visibleLeft, visibleTop, bgWidth, skyBottom - visibleTop);

    const green = ctx.createRadialGradient(this.width - 320, 62, 10, this.width - 320, 62, 380);
    green.addColorStop(0, 'rgba(101, 163, 13, 0.15)');
    green.addColorStop(0.5, 'rgba(63, 98, 18, 0.08)');
    green.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = green;
    ctx.fillRect(visibleLeft, visibleTop, bgWidth, skyBottom - visibleTop);

    // Hive Moon disc + craters, static frame (flare/satellite are dynamic-only)
    const moonX = 1300;
    const moonY = 60;
    const moonR = 46;
    const haze = ctx.createRadialGradient(moonX, moonY, moonR * 0.4, moonX, moonY, moonR * 4.6);
    haze.addColorStop(0, 'rgba(226, 232, 220, 0.2)');
    haze.addColorStop(0.4, 'rgba(190, 210, 190, 0.1)');
    haze.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = haze;
    ctx.beginPath();
    ctx.arc(moonX, moonY, moonR * 4.6, 0, Math.PI * 2);
    ctx.fill();
    const body = ctx.createRadialGradient(moonX - moonR * 0.3, moonY - moonR * 0.3, moonR * 0.1, moonX, moonY, moonR);
    body.addColorStop(0, 'rgba(206, 196, 205, 0.95)');
    body.addColorStop(0.45, 'rgba(130, 128, 132, 0.9)');
    body.addColorStop(0.8, 'rgba(64, 60, 66, 0.92)');
    body.addColorStop(1, 'rgba(26, 24, 28, 0.95)');
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.arc(moonX, moonY, moonR, 0, Math.PI * 2);
    ctx.fill();
    ctx.save();
    ctx.beginPath();
    ctx.arc(moonX, moonY, moonR, 0, Math.PI * 2);
    ctx.clip();
    for (const c of MOON_CRATERS) drawMoonCrater(ctx, moonX + c.dx * moonR, moonY + c.dy * moonR, c.r * moonR);
    ctx.restore();
    const limb = ctx.createRadialGradient(moonX, moonY, moonR * 0.75, moonX, moonY, moonR * 1.25);
    limb.addColorStop(0, 'rgba(0, 0, 0, 0)');
    limb.addColorStop(0.7, 'rgba(190, 210, 190, 0.12)');
    limb.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = limb;
    ctx.beginPath();
    ctx.arc(moonX, moonY, moonR * 1.25, 0, Math.PI * 2);
    ctx.fill();

    // Two ridge layers
    const drawRidge = (list, color) => {
      if (!list || !list.length) return;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(list[0].x, laneY);
      for (const p of list) {
        ctx.lineTo(p.x, laneY);
        ctx.lineTo(p.x + p.w / 2, laneY - p.h);
        ctx.lineTo(p.x + p.w, laneY);
      }
      ctx.lineTo(list[list.length - 1].x + list[list.length - 1].w, laneY);
      ctx.closePath();
      ctx.fill();
    };
    drawRidge(layout.farRidge, 'rgba(58, 52, 74, 0.26)');
    drawRidge(layout.nearRidge, 'rgba(80, 72, 98, 0.36)');
    const ridgeHaze = ctx.createLinearGradient(0, laneY - 90, 0, laneY);
    ridgeHaze.addColorStop(0, 'rgba(148, 163, 184, 0)');
    ridgeHaze.addColorStop(1, 'rgba(148, 163, 184, 0.07)');
    ctx.fillStyle = ridgeHaze;
    ctx.fillRect(visibleLeft, laneY - 90, bgWidth, 90);

    for (const p of layout.pyramids) drawPyramid(ctx, p, laneY, this.quality);
    // 'low' never runs renderSulDynamicBackground (Paradigm 10), so it would
    // otherwise lose the vent glow entirely now that drawPyramid() no longer
    // draws it - bake it here too, but only for 'low', at the exact same
    // frozen pulse=0.7 value low already got before this fix (see
    // drawPyramidVentGlow's own comment). Every other tier's vent glow now
    // comes from the dynamic pass instead, actually animating in-game.
    if (this.quality === 'low') {
      for (const p of layout.pyramids) drawPyramidVentGlow(ctx, p, laneY, t, this.quality);
    }
    for (const pod of layout.biomassPods) drawBiomassPod(ctx, pod, t, this.quality);
    for (const r of layout.backRocks) drawBackRock(ctx, r);

    // Floor: ONE continuous gradient from the horizon all the way down to
    // visibleBottom (lane + shelf together), matching the standalone prototype
    // (sandbox/sul-map) where the floor gradient seamlessly descends all the
    // way to the bottom of the canvas. Fractions are computed relative to
    // shelfBottom so the shelf inherits the smooth dark-plum descent rather
    // than abruptly flattening at laneBottom.
    const laneBottom = laneY + laneH;
    const shelfBottom = Math.max(this.height, laneBottom + 44);
    const totalSpan = Math.max(1, visibleBottom - laneY);
    const shelfFrac = Math.min(1, (shelfBottom - laneY) / totalSpan);
    const floorGrad = ctx.createLinearGradient(0, laneY, 0, visibleBottom);
    floorGrad.addColorStop(0, '#241a22');
    floorGrad.addColorStop(0.4 * shelfFrac, '#1a1119');
    floorGrad.addColorStop(0.72 * shelfFrac, '#0d0810');
    floorGrad.addColorStop(shelfFrac, '#050308');
    if (shelfFrac < 1) floorGrad.addColorStop(1, '#050308');
    ctx.fillStyle = floorGrad;
    ctx.fillRect(visibleLeft, laneY, bgWidth, visibleBottom - laneY);

    for (const p of layout.floorDetail.patches) {
      if (p.edgeFade <= 0.01) continue;
      ctx.save();
      ctx.globalAlpha = p.edgeFade;
      const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r);
      g.addColorStop(0, p.dark ? 'rgba(0, 0, 0, 0.18)' : 'rgba(90, 70, 85, 0.12)');
      g.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    for (const r of layout.floorDetail.rubble) {
      if (r.edgeFade <= 0.01) continue;
      ctx.save();
      ctx.globalAlpha = r.edgeFade;
      ctx.translate(r.x, r.y);
      ctx.rotate(r.rot);
      const base = 20 + r.shade * 20;
      ctx.fillStyle = `rgba(${base + 10}, ${base}, ${base + 8}, 0.8)`;
      ctx.fillRect(-r.w / 2, -r.h / 2, r.w, r.h);
      ctx.fillStyle = `rgba(${base + 30}, ${base + 22}, ${base + 26}, 0.7)`;
      ctx.fillRect(-r.w / 2, -r.h / 2, r.w, r.h * 0.4);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
      ctx.fillRect(-r.w / 2, r.h * 0.15, r.w, r.h * 0.35);
      ctx.restore();
    }
    ctx.lineWidth = 2;
    for (const vein of layout.floorVeins) {
      ctx.strokeStyle = `rgba(217, 70, 239, ${vein.alpha})`;
      ctx.beginPath();
      vein.pts.forEach((pt, i) => (i === 0 ? ctx.moveTo(pt.x, pt.y) : ctx.lineTo(pt.x, pt.y)));
      ctx.stroke();
    }

    ctx.save();
    ctx.globalAlpha = 0.5;
    ctx.drawImage(getGrainFadeCanvas(bgWidth, laneH), 0, 0, bgWidth, laneH, visibleLeft, laneY, bgWidth, laneH);
    ctx.restore();

    for (const p of layout.plasmaTraces) {
      const rgb = p.hue === 'green' ? '101, 163, 13' : '217, 70, 239';
      const x2 = p.x + Math.cos(p.angle) * p.len;
      const y2 = p.y + Math.sin(p.angle) * p.len;
      const midX = (p.x + x2) / 2 + Math.cos(p.angle + Math.PI / 2) * p.curve * p.len;
      const midY = (p.y + y2) / 2 + Math.sin(p.angle + Math.PI / 2) * p.curve * p.len;
      ctx.strokeStyle = `rgba(${rgb}, 0.16)`;
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.quadraticCurveTo(midX, midY, x2, y2);
      ctx.stroke();
      ctx.strokeStyle = `rgba(${rgb}, 0.4)`;
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.quadraticCurveTo(midX, midY, x2, y2);
      ctx.stroke();
    }

    const horizonHaze = ctx.createLinearGradient(0, laneY, 0, laneY + 50);
    horizonHaze.addColorStop(0, 'rgba(96, 88, 116, 0.4)');
    horizonHaze.addColorStop(1, 'rgba(96, 88, 116, 0)');
    ctx.fillStyle = horizonHaze;
    ctx.fillRect(visibleLeft, laneY, bgWidth, 50);

    // Letterbox veil: an alpha fade to the exact page background color
    // (css/main.css --bg-dark: #070b12, confirmed live via
    // getComputedStyle(document.body).backgroundColor === 'rgb(7, 11, 18)'),
    // reaching full opacity precisely at this.height - not visibleBottom,
    // which is the padded bake-buffer edge. Starts only at laneBottom
    // so the combat lane itself is never obscured or tinted.
    if (visibleBottom > laneBottom) {
      const veilTop = laneBottom;
      const veilBottom = Math.max(veilTop + 1, this.height);
      const veil = ctx.createLinearGradient(0, veilTop, 0, veilBottom);
      veil.addColorStop(0, 'rgba(7, 11, 18, 0)');
      veil.addColorStop(1, 'rgba(7, 11, 18, 1)');
      ctx.fillStyle = veil;
      ctx.fillRect(visibleLeft, veilTop, bgWidth, veilBottom - veilTop);
      if (visibleBottom > veilBottom) {
        ctx.fillStyle = 'rgb(7, 11, 18)';
        ctx.fillRect(visibleLeft, veilBottom, bgWidth, visibleBottom - veilBottom);
      }
    }
  },

  renderSulDynamicBackground(ctx, battlefield, nowSec = null, customBounds = null) {
    const t = nowSec !== null ? nowSec : performance.now() * 0.001;
    const laneY = battlefield.laneY;
    const layout = getLayout(this, battlefield);
    const skyBottom = laneY + 2;
    const quality = this.quality;
    const isMedium = quality === 'medium';

    // Viewport culling (item 1): layout.starfield/driftFragments span the
    // full camera-pan buffer (buildLayout's minX/maxX, ~2.5x the visible
    // viewport width - see buildLayout's padX), so most of them are off
    // camera on any given frame. customBounds is the renderer's already-
    // padded currently-visible region (renderBackgroundCached's
    // dynamicBgBounds) - resolveBounds() falls back to the viewport's own
    // bounds when it's null (e.g. direct/test calls), same as the static
    // bake functions already do. A small extra margin keeps stars/fragments
    // from visibly popping at the cull edge.
    const { visibleLeft, visibleRight } = resolveBounds(this, customBounds);
    const cullMargin = 30;
    const cullLeft = visibleLeft - cullMargin;
    const cullRight = visibleRight + cullMargin;

    const starfield = layout.starfield;
    for (let i = 0; i < starfield.length; i++) {
      const s = starfield[i];
      if (s.x < cullLeft - s.r || s.x > cullRight + s.r) continue;
      // Medium tier (item 2): half the density still reads as a starfield.
      if (isMedium && (i & 1) === 1) continue;
      const twinkle = 0.6 + 0.4 * Math.sin(t * 2 + s.twinklePhase);
      ctx.fillStyle = s.tint === 'violet' ? `rgba(216, 180, 254, ${0.15 * twinkle})` : `rgba(226, 232, 240, ${0.2 * twinkle})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();
    }

    const minX = layout.minX !== undefined ? layout.minX : -20;
    const totalW = layout.totalFieldW !== undefined ? layout.totalFieldW : (this.width + 40);
    const driftFragments = layout.driftFragments;
    for (let i = 0; i < driftFragments.length; i++) {
      if (isMedium && (i & 1) === 1) continue;
      const f = driftFragments[i];
      const x = minX + (((f.x - minX + t * f.driftSpeed) % totalW) + totalW) % totalW;
      if (x < cullLeft - f.r || x > cullRight + f.r) continue;
      const bob = Math.sin(t * 0.6 + f.phase) * 4;
      ctx.fillStyle = 'rgba(45, 38, 52, 0.8)';
      ctx.beginPath();
      ctx.ellipse(x, f.y + bob, f.r, f.r * 0.7, f.phase, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(217, 70, 239, 0.2)';
      ctx.beginPath();
      ctx.ellipse(x - f.r * 0.2, f.y + bob - f.r * 0.15, f.r * 0.3, f.r * 0.2, f.phase, 0, Math.PI * 2);
      ctx.fill();
    }

    // Rare meteor streak
    const METEOR_CYCLE = 12;
    const METEOR_ACTIVE = 1.1;
    const meteorOffset = this._sulMeteorOffset ?? (this._sulMeteorOffset = Math.random() * METEOR_CYCLE);
    const cyclePos = (t + meteorOffset) % METEOR_CYCLE;
    if (cyclePos <= METEOR_ACTIVE) {
      const cycleIndex = Math.floor((t + meteorOffset) / METEOR_CYCLE);
      const rng = mulberry32(cycleIndex * 7919 + 11);
      const startX = rng() * this.width * 0.6;
      const startY = 10 + rng() * 50;
      const angle = 0.45 + rng() * 0.25;
      const len = 90 + rng() * 60;
      const progress = cyclePos / METEOR_ACTIVE;
      const fade = progress < 0.15 ? progress / 0.15 : progress > 0.7 ? (1 - progress) / 0.3 : 1;
      const headX = startX + Math.cos(angle) * len * progress;
      const headY = startY + Math.sin(angle) * len * progress;
      const tailX = headX - Math.cos(angle) * len * 0.4;
      const tailY = headY - Math.sin(angle) * len * 0.4;
      ctx.strokeStyle = `rgba(220, 240, 230, ${0.85 * fade})`;
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(headX, headY);
      ctx.stroke();
      const mGlow = getMeteorGlowCanvas();
      if (mGlow) {
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.drawImage(mGlow, headX - 8, headY - 8);
        ctx.restore();
      }
    }

    // Moon flare + satellite (disc/craters already baked - only the
    // brightness pulse and the orbiting moonlet change per frame)
    const moonX = 1300;
    const moonY = 60;
    const moonR = 46;
    const baseFlicker = 0.85 + 0.15 * Math.sin(t * 1.3);
    const FLARE_CYCLE = 11;
    const flarePos = t % FLARE_CYCLE;
    const flare = flarePos < 2.2 ? Math.sin((flarePos / 2.2) * Math.PI) : 0;
    const pulse = baseFlicker + flare * 0.7;
    if (flare > 0.02) {
      const flareCanvas = getSulMoonFlareCanvas();
      if (flareCanvas) {
        ctx.save();
        ctx.globalAlpha = flare;
        const r = Math.ceil(moonR * 2.2);
        ctx.drawImage(flareCanvas, moonX - r, moonY - r);
        ctx.restore();
      }
    }
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    const targetY = skyBottom + 40;
    for (const ray of MOON_RAYS) {
      const topX1 = moonX + ray.off * 0.05 - ray.wTop / 2;
      const topX2 = moonX + ray.off * 0.05 + ray.wTop / 2;
      const botX = moonX + ray.off;
      const botX1 = botX - ray.wBot / 2;
      const botX2 = botX + ray.wBot / 2;
      const rayGrad = getMoonRayGrad(ctx, moonX, moonY, botX, targetY);
      ctx.save();
      ctx.globalAlpha = pulse * ray.alphaMul;
      ctx.fillStyle = rayGrad;
      ctx.beginPath();
      ctx.moveTo(topX1, moonY);
      ctx.lineTo(topX2, moonY);
      ctx.lineTo(botX2, targetY);
      ctx.lineTo(botX1, targetY);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
    ctx.restore();
    const sat = satellitePosition(moonX, moonY, t);
    if (!sat.behind) drawSatellite(ctx, sat.x, sat.y);

    // Back-rock tentacle sway - leans/whips harder during a spore-wind gust.
    // Medium tier (item 2): every other tentacle is frozen at its rest pose
    // (t=0, windBoost=1) instead of swaying, cutting the per-frame trig/sway
    // work roughly in half while the rest keep the shelf visibly alive.
    const windBoost = 1 + this.getSulWeatherState().windIntensity * 2.2;
    let tentacleIdx = 0;
    for (const r of layout.backRocks) {
      if (r.tentacle) {
        const freeze = isMedium && (tentacleIdx & 1) === 1;
        drawTentacle(
          ctx,
          r.tentacle,
          freeze ? 0 : t,
          {
            color: 'rgba(160, 130, 190, 0.85)',
            glowColor: 'rgba(200, 180, 230, 0.55)',
            baseWidth: 2.4
          },
          freeze ? 1 : windBoost
        );
        tentacleIdx++;
      }
    }

    // Pyramid vent pulse (item 5 fix): structural body stays baked statically
    // (drawPyramid above, in renderSulStaticBackground); only this glow layer
    // is time-varying, so it now runs here, every frame, instead of being
    // frozen into the static bake. drawPyramidVentGlow() itself bails out
    // immediately for pyramids without a vent.
    for (const p of layout.pyramids) drawPyramidVentGlow(ctx, p, laneY, t, quality);

    // Slime drips
    for (const drip of layout.slimeDrips) {
      const progress = ((t + drip.phase) % drip.period) / drip.period;
      const y = drip.startY + (drip.endY - drip.startY) * progress;
      const trailTop = Math.max(drip.startY, y - 18);
      const trail = getSlimeDripTrailGrad(ctx);
      ctx.save();
      ctx.translate(drip.x, trailTop);
      ctx.strokeStyle = trail;
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, y - trailTop);
      ctx.stroke();
      ctx.restore();
      ctx.fillStyle = 'rgba(132, 204, 22, 0.7)';
      ctx.beginPath();
      ctx.ellipse(drip.x, y, 2.2, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      if (progress > 0.9) {
        const splatFade = (progress - 0.9) / 0.1;
        ctx.strokeStyle = `rgba(132, 204, 22, ${0.35 * (1 - splatFade)})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(drip.x, drip.endY, 3 + splatFade * 9, 1.5 + splatFade * 3, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  },

  // Static foreground props on the shelf below the combat lane.
  // Draws the single stylized obsidian rock under the indicator/midline area,
  // grounding the bottom shelf in front of the neon rail without creating any
  // full-width rectangular fill discontinuities.
  renderSulStaticForeground(ctx, battlefield, customBounds = null) {
    const layout = getLayout(this, battlefield);
    if (layout.indicatorRock) {
      drawShelfRock(ctx, layout.indicatorRock);
    }
  },

  renderSulDynamicForeground(ctx, battlefield, nowSec = null) {
    const t = nowSec !== null ? nowSec : performance.now() * 0.001;
    const layout = getLayout(this, battlefield);
    const windBoost = 1 + this.getSulWeatherState().windIntensity * 2.2;
    const isMedium = this.quality === 'medium';
    // Medium tier (item 2): every other alien plant skips its sway/breathe
    // computation and draws statically instead - see drawAlienPlant's
    // swayEnabled parameter. The lone indicatorPlant always animates (it's
    // a single accent, not a density lever).
    const alienPlants = layout.alienPlants;
    for (let i = 0; i < alienPlants.length; i++) {
      const swayEnabled = !(isMedium && (i & 1) === 1);
      drawAlienPlant(ctx, alienPlants[i], t, this.quality, windBoost, swayEnabled);
    }
    if (layout.indicatorPlant) drawAlienPlant(ctx, layout.indicatorPlant, t, this.quality, windBoost);

    const period = battlefield.laneY + battlefield.laneHeight + 60;
    const minX = layout.minX !== undefined ? layout.minX : 0;
    const totalW = layout.totalFieldW !== undefined ? layout.totalFieldW : this.width;
    for (const s of layout.sporesFront) {
      const travel = (t * -s.speedY) % period;
      const y = s.y - travel < battlefield.laneY + battlefield.laneHeight - 20 ? s.y - travel + period : s.y - travel;
      const rawX = s.x + Math.sin(t * 0.5 + s.phase) * 10 + t * s.driftX * 0.1;
      const wrappedX = minX + (((rawX - minX) % totalW) + totalW) % totalW;
      const flicker = 0.5 + 0.5 * Math.sin(t * 1.6 + s.phase);
      // Item 3: cached LUT sprite instead of a fresh createRadialGradient()
      // per spore per frame - see getSporeGlowLUT(). globalAlpha reproduces
      // the original "0.6 * flicker" center alpha (LUT bakes the 0.6).
      const r = s.r * 3.2;
      ctx.globalAlpha = flicker;
      ctx.drawImage(getSporeGlowLUT(s.hue), wrappedX - r, y - r, r * 2, r * 2);
    }
    ctx.globalAlpha = 1;
  },

  // Paced/rare Sul fauna on the foreground shelf, same cadence as the human
  // map's renderForegroundCritters (max concurrent kept low - see the
  // sandbox notes on why "insgesamt weniger" mattered). Reuses
  // this.foregroundCritters (already initialized in the Renderer
  // constructor) rather than a second array.
  renderSulForegroundCritters(ctx, battlefield, nowSec = null) {
    const t = nowSec !== null ? nowSec : performance.now() * 0.001;
    const laneBottom = battlefield.laneY + battlefield.laneHeight;
    const shelfH = 44;

    const bounds = this.viewport ? this.viewport.getVisibleBounds() : null;
    const viewLeft = bounds ? Math.min(-40, bounds.left - 40) : -40;
    const viewRight = bounds ? Math.max(this.width + 40, bounds.right + 40) : this.width + 40;

    this._sulCritterTimer = (this._sulCritterTimer || 0) + 1 / 60;
    this._sulBottomCritterTimer = (this._sulBottomCritterTimer || 0) + 1 / 60;

    if (this._sulCritterTimer >= 8) {
      this._sulCritterTimer = 0;
      if (this.foregroundCritters.length < 1 && Math.random() < 0.85) {
        const types = ['grub', 'scuttler', 'drifter'];
        const type = types[Math.floor(Math.random() * types.length)];
        const dir = Math.random() > 0.5 ? 1 : -1;
        const groundY = laneBottom + shelfH * 0.4 + Math.random() * shelfH * 0.45;
        this.foregroundCritters.push({
          type,
          x: dir === 1 ? viewLeft : viewRight,
          y: type === 'drifter' ? groundY - 10 : groundY,
          dir,
          speed: type === 'scuttler' ? 58 : type === 'drifter' ? 34 : 22,
          animTimer: Math.random() * 10,
          pauseTimer: 0
        });
      }
    }
    if (this._sulBottomCritterTimer >= 9) {
      this._sulBottomCritterTimer = 0;
      if (!this._sulBottomCritters) this._sulBottomCritters = [];
      if (this._sulBottomCritters.length < 1 && Math.random() < 0.85) {
        const dir = Math.random() > 0.5 ? 1 : -1;
        this._sulBottomCritters.push({
          type: 'hulker',
          x: dir === 1 ? viewLeft - 10 : viewRight + 10,
          y: laneBottom + shelfH * 0.72 + Math.random() * shelfH * 0.24,
          dir,
          speed: 13,
          animTimer: Math.random() * 10,
          pauseTimer: 0
        });
      }
    }
    if (!this._sulBottomCritters) this._sulBottomCritters = [];

    const stepList = (list) => {
      for (let i = list.length - 1; i >= 0; i--) {
        const cr = list[i];
        cr.animTimer += 1 / 60;
        if (cr.pauseTimer > 0) {
          cr.pauseTimer -= 1 / 60;
        } else if (Math.random() < 0.006 && cr.x > viewLeft + 150 && cr.x < viewRight - 150) {
          cr.pauseTimer = 0.6 + Math.random() * 0.8;
        } else {
          cr.x += cr.dir * cr.speed * (1 / 60);
        }
        if ((cr.dir === 1 && cr.x > viewRight + 60) || (cr.dir === -1 && cr.x < viewLeft - 60)) list.splice(i, 1);
      }
    };
    stepList(this.foregroundCritters);
    stepList(this._sulBottomCritters);

    for (const cr of this.foregroundCritters) drawSulCritter(ctx, cr);
    for (const cr of this._sulBottomCritters) drawSulBottomCritter(ctx, cr);
  },

  // Analogous to weather.js's getWeatherState() (human map: rain + a
  // playful wind gust) but Sul-specific: no rain (nothing about this world
  // suggests storm clouds), just a periodic spore-wind gust. Lightning is
  // its own, separately-paced event (see drawPlasmaLightning) rather than
  // tied to this cycle, since it reads better as an irregular structural
  // discharge than a weather-synced flash.
  getSulWeatherState() {
    const t = performance.now() * 0.001;
    const period = 54; // 54s cycle
    const windStart = 20; // gust starts at 20s
    const windDuration = 16; // lasts 16s (20s to 36s), ramps over 2.5s each end
    const cyclePos = t % period;

    let windIntensity = 0;
    if (cyclePos >= windStart && cyclePos < windStart + windDuration) {
      const age = cyclePos - windStart;
      windIntensity = Math.min(1, Math.min(age / 2.5, (windDuration - age) / 2.5));
    }

    if (!this._sulWeatherState) {
      this._sulWeatherState = { t: 0, windIntensity: 0, cyclePos: 0, windStart, windDuration, period };
    }
    const state = this._sulWeatherState;
    state.t = t;
    state.windIntensity = windIntensity;
    state.cyclePos = cyclePos;
    return state;
  },

  // Behind units: distant/dim half of the spore-wind pool, plus the
  // plasma-arc lightning (a background-depth event between two pyramids).
  renderSulWeatherBackground(ctx, battlefield) {
    const t = performance.now() * 0.001;
    const layout = getLayout(this, battlefield);
    const { windIntensity } = this.getSulWeatherState();

    if (!this._sulLightningOffset) this._sulLightningOffset = {};
    const flash = drawPlasmaLightning(ctx, layout, battlefield.laneY, t, this._sulLightningOffset);
    if (flash > 0.01) {
      const bounds = this.viewport ? this.viewport.getVisibleBounds() : null;
      const vLeft = bounds ? Math.min(0, bounds.left - 40) : -40;
      const vRight = bounds ? Math.max(this.width, bounds.right + 40) : this.width + 40;
      const vTop = bounds ? Math.min(0, bounds.top - 40) : -40;
      const vBottom = bounds ? Math.max(this.height, bounds.bottom + 40) : this.height + 40;
      ctx.fillStyle = `rgba(225, 210, 255, ${0.05 * flash})`;
      ctx.fillRect(vLeft, vTop, vRight - vLeft, vBottom - vTop);
    }

    if (windIntensity > 0.01) {
      // Medium tier (item 2): half the wind-spore count during a gust - the
      // event is already rare/brief, so full density isn't needed to read.
      const isMedium = this.quality === 'medium';
      const windSpores = layout.windSpores;
      for (let i = 0; i < windSpores.length; i++) {
        if (isMedium && (i & 1) === 1) continue;
        const s = windSpores[i];
        if (s.layer === 'back') drawWindSpore(ctx, s, t, windIntensity, layout.minX, layout.totalFieldW);
      }
    }
  },

  // In front of everything (including units): the closer, bigger, brighter
  // half of the spore-wind pool - same depth split as the human map's own
  // rain/wind foreground layer.
  renderSulWeather(ctx, battlefield) {
    const t = performance.now() * 0.001;
    const { windIntensity } = this.getSulWeatherState();
    if (windIntensity <= 0.01) return;
    const layout = getLayout(this, battlefield);
    const isMedium = this.quality === 'medium';
    const windSpores = layout.windSpores;
    for (let i = 0; i < windSpores.length; i++) {
      if (isMedium && (i & 1) === 1) continue;
      const s = windSpores[i];
      if (s.layer === 'front') drawWindSpore(ctx, s, t, windIntensity, layout.minX, layout.totalFieldW);
    }
  }
};

function drawSulCritter(ctx, cr) {
  ctx.save();
  ctx.translate(cr.x, cr.y);
  if (cr.dir === -1) ctx.scale(-1, 1);

  if (cr.type === 'grub') {
    const hunch = Math.sin(cr.animTimer * 6) * 2;
    ctx.fillStyle = 'rgba(74, 65, 82, 0.95)';
    ctx.beginPath();
    ctx.ellipse(-4 + hunch * 0.3, 0, 7, 3.4, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(4 - hunch * 0.3, -0.5, 5.5, 3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(132, 204, 22, 0.85)';
    for (let s = 0; s < 3; s++) {
      ctx.beginPath();
      ctx.arc(-6 + s * 5, -1.5, 0.8, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (cr.type === 'scuttler') {
    const legStep = Math.sin(cr.animTimer * 24) > 0 ? 1.4 : -1.4;
    ctx.strokeStyle = 'rgba(42, 36, 48, 0.9)';
    ctx.lineWidth = 1.2;
    for (let s = -1; s <= 1; s++) {
      ctx.beginPath();
      ctx.moveTo(s * 3, -1);
      ctx.lineTo(s * 5, 3 + legStep * (s === 0 ? -1 : 1) * 0.5);
      ctx.stroke();
    }
    ctx.fillStyle = 'rgba(58, 50, 66, 0.95)';
    ctx.beginPath();
    ctx.ellipse(0, -2, 6, 3.6, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(217, 70, 239, 0.8)';
    ctx.beginPath();
    ctx.arc(4, -3, 0.9, 0, Math.PI * 2);
    ctx.fill();
  } else {
    const bob = Math.sin(cr.animTimer * 3) * 4;
    ctx.strokeStyle = 'rgba(217, 70, 239, 0.5)';
    ctx.lineWidth = 0.8;
    for (let s = -1; s <= 1; s += 2) {
      ctx.beginPath();
      ctx.moveTo(0, bob);
      ctx.lineTo(-6 + s * 2, bob + 5 + Math.sin(cr.animTimer * 5 + s) * 2);
      ctx.stroke();
    }
    const wGlow = getSulWispGlowCanvas();
    if (wGlow) {
      ctx.drawImage(wGlow, -8, bob - 8);
    }
    ctx.fillStyle = 'rgba(226, 232, 220, 0.85)';
    ctx.beginPath();
    ctx.ellipse(0, bob, 3, 2.2, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawSulBottomCritter(ctx, cr) {
  ctx.save();
  ctx.translate(cr.x, cr.y);
  if (cr.dir === -1) ctx.scale(-1, 1);

  const limbWave = Math.sin(cr.animTimer * 4);
  ctx.strokeStyle = 'rgba(48, 42, 56, 0.9)';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  for (let i = -1; i <= 1; i++) {
    const wave = Math.sin(cr.animTimer * 4 + i * 1.1) * 5;
    ctx.beginPath();
    ctx.moveTo(i * 7, -2);
    ctx.quadraticCurveTo(i * 9 + wave * 0.4, 6, i * 6 + wave, 12);
    ctx.stroke();
  }
  const body = getSulBottomCritterBodyGrad(ctx);
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.ellipse(0, -4 + limbWave * 0.5, 18, 10, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(101, 163, 13, 0.7)';
  for (let s = 0; s < 3; s++) {
    ctx.beginPath();
    ctx.arc(-10 + s * 9, -6, 1.3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = 'rgba(217, 70, 239, 0.85)';
  ctx.beginPath();
  ctx.arc(13, -7, 1.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
